import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { toast } from 'd3components/src/utils/tools';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { i18n } from '@/i18n';

const t = i18n.global.t;


// 退出管理器配置接口
export interface ExitManagerConfig {
  enabled: boolean;           // 是否启用滑动退出
  confirmTimeout: number;     // 确认超时时间（毫秒）
  hapticStyle: ImpactStyle;   // 触觉反馈样式
  showToast: boolean;         // 是否显示提示
  exitDelay: number;          // 退出延迟时间
  toastMessage: string;       // 提示消息
}

// 退出管理器类
// 负责处理应用退出的确认逻辑和用户体验
export class ExitManager {
  private static instance: ExitManager;
  private exitIntent: boolean = false;
  private exitTimer: number | null = null;
  private config: ExitManagerConfig;
  private exitCallbacks: Array<() => void> = [];
  private lastHandleExitIntentTime: number = 0;
  private readonly HANDLE_EXIT_INTENT_COOLDOWN: number = 100; // 100ms冷却时间，防止系统双重触发

  constructor(config?: Partial<ExitManagerConfig>) {
    this.config = {
      enabled: true,
      confirmTimeout: 2000,
      hapticStyle: ImpactStyle.Medium,
      showToast: true,
      exitDelay: 300,
      toastMessage: t('exited.toastMessage'),
      ...config
    };
  }

  // 获取单例实例
  static getInstance(config?: Partial<ExitManagerConfig>): ExitManager {
    if (!ExitManager.instance) {
      ExitManager.instance = new ExitManager(config);
    }
    return ExitManager.instance;
  }

  // 处理退出意图
  // @param source 触发源（'swipe' | 'hardware' | 'manual'）
  // @param isQualifiedExitGesture 是否为合格的退出手势（用于滑动）
  handleExitIntent(source: 'swipe' | 'hardware' | 'manual' = 'swipe', isQualifiedExitGesture: boolean = true): boolean {
    console.log(`[ExitManager] handleExitIntent 被调用， source: ${source}, qualified: ${isQualifiedExitGesture}`)
    console.log(`[ExitManager] ExitManager 配置 enabled: ${this.config.enabled}`)
    console.log(`[ExitManager] 当前退出意图: ${this.exitIntent}`)

    // 防止在冷却期内重复调用（系统双重触发）
    const now = Date.now();
    if (now - this.lastHandleExitIntentTime < this.HANDLE_EXIT_INTENT_COOLDOWN) {
      console.log(`[ExitManager] 在冷却期内检测到重复调用，这可能是系统双重触发`);
      // 如果exitIntent为false，说明这是第一次调用的回声，直接忽略
      if (!this.exitIntent) {
        console.log(`[ExitManager] exitIntent为false，忽略此次重复调用`);
        return false;
      }
      // 如果exitIntent为true，继续处理（允许用户在2秒内第二次按返回键退出）
    }
    this.lastHandleExitIntentTime = now;

    if (!this.config.enabled) {
      console.log(`[ExitManager] ExitManager 配置 disabled，返回 false`)
      return false;
    }

    // 对于滑动手势，只有合格的退出手势才处理
    if (source === 'swipe' && !isQualifiedExitGesture) {
      console.log(`[ExitManager] 滑动手势不合法，忽略`)
      return false;
    }

    if (this.exitIntent) {
      // 已有退出意图，执行退出（不再显示toast）
      console.log(`[ExitManager] 已有退出意图，直接执行退出`)
      this.executeExit();
      return true;
    } else {
      // 第一次设置退出意图并显示提示
      console.log(`[ExitManager] 第一次设置退出意图，显示提示`)
         this.exitIntent = true;
    
    // 显示退出提示
    if (this.config.showToast) {
          console.log(`[ExitManager] 显示toast: ${this.config.toastMessage}`)
          toast(this.config.toastMessage, 2);

    }

    // 触发震动反馈
    this.triggerVibration();

    // 通知监听器
    this.notifyExitIntentSet();

    // 设置超时清除
    this.clearExitTimer();
    this.exitTimer = window.setTimeout(() => {
      this.clearExitIntent();
    }, this.config.confirmTimeout);
      return false;
    }
  }
  // 清除退出意图
  private clearExitIntent(): void {
    this.exitIntent = false;
    this.clearExitTimer();
    this.notifyExitIntentCleared();
    console.log(' [ExitManager] 退出意图清除');
  }

  // 执行退出
  private executeExit(): void {
    console.log('[ExitManager] 执行退出...');
    
    // 清除退出意图
    this.clearExitIntent();

    // 延迟退出，让动画完成
    setTimeout(() => {
      if (Capacitor.isNativePlatform()) {
        App.exitApp();
      } else {
        // Web 环境下的处理
        if (window.confirm('确定要退出应用吗？')) {
          window.close();
        }
      }
    }, this.config.exitDelay);
  }

  // 触发触觉反馈
  private triggerVibration(): void {
    try {
      Haptics.impact({ style: this.config.hapticStyle });
    } catch (error) {
      console.warn('触发触觉反馈失败:', error);
    }
  }

  // 清除退出定时器
  private clearExitTimer(): void {
    if (this.exitTimer) {
      clearTimeout(this.exitTimer);
      this.exitTimer = null;
    }
  }

  // 通知退出意图已设置
  private notifyExitIntentSet(): void {
    // 可以在这里触发自定义事件或调用回调
    window.dispatchEvent(new CustomEvent('退出意图已设置'));
  }

  // 通知退出意图已清除
  private notifyExitIntentCleared(): void {
    // 可以在这里触发自定义事件或调用回调
    window.dispatchEvent(new CustomEvent('退出意图已清除'));
  }

  // 添加退出回调
  public addExitCallback(callback: () => void): void {
    this.exitCallbacks.push(callback);
  }

  // 移除退出回调
  public removeExitCallback(callback: () => void): void {
    const index = this.exitCallbacks.indexOf(callback);
    if (index > -1) {
      this.exitCallbacks.splice(index, 1);
    }
  }

  // 手动触发退出（无确认）
  public forceExit(): void {
    console.log('手动触发退出');
    this.executeExit();
  }

  // 检查是否有退出意图
  public hasExitIntent(): boolean {
    return this.exitIntent;
  }

  // 更新配置
  public updateConfig(newConfig: Partial<ExitManagerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  // 获取当前配置
  public getConfig(): ExitManagerConfig {
    return { ...this.config };
  }

  // 重置退出管理器状态
  public reset(): void {
    this.clearExitIntent();
    this.exitCallbacks = [];
  }

  // 启用退出功能
  public enable(): void {
    this.config.enabled = true;
    console.log('启用退出功能');
  }

  // 禁用退出功能
  public disable(): void {
    this.config.enabled = false;
    this.clearExitIntent();
    console.log('禁用退出功能');
  }
}

// 导出默认实例
export const exitManager = ExitManager.getInstance();
